import { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { provider, bullvsbear } from '../../../.config';
import BullvsBearABI from '../../../abi/bullvsbearABI.json';

const FEED_ABI = [
  'function latestRoundData() view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)',
  'function getRoundData(uint80 _roundId) view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)',
  'function decimals() view returns (uint8)',
];
const BATCH = 15;
const MAX_BATCHES = 8;

// the game's own Chainlink feed, looked up once
let feedPromise = null;
const getFeed = () => {
  if (!feedPromise) {
    feedPromise = (async () => {
      const game = new ethers.Contract(bullvsbear, BullvsBearABI, provider);
      const feed = new ethers.Contract(await game.oracle(), FEED_ABI, provider);
      return { feed, decimals: await feed.decimals() };
    })().catch((error) => {
      feedPromise = null;
      throw error;
    });
  }
  return feedPromise;
};

/**
 * Feed answers from `fromId` down to (not including) `stopId`, while they are newer than `since`, oldest first.
 * Reads in parallel batches; a round the feed cannot return (a new aggregator phase) ends the walk.
 */
const walkBack = async (feed, decimals, fromId, stopId, since) => {
  const out = [];
  let id = fromId;
  for (let batch = 0; batch < MAX_BATCHES; batch += 1) {
    const ids = [];
    while (ids.length < BATCH && id.gt(stopId) && id.gt(0)) {
      ids.push(id);
      id = id.sub(1);
    }
    if (!ids.length) break;
    const rows = await Promise.all(ids.map((roundId) => feed.getRoundData(roundId).catch(() => null)));
    const stop = rows.findIndex((row) => !row || row.updatedAt.toNumber() < since);
    rows.slice(0, stop === -1 ? rows.length : stop).forEach((row) => {
      out.push({ t: row.updatedAt.toNumber(), p: Number(ethers.utils.formatUnits(row.answer, decimals)) });
    });
    if (stop !== -1) break;
  }
  return out.reverse();
};

/**
 * Oracle prices published since `since` (unix seconds) as [{ t, p }], oldest first. `tick` triggers a refresh
 * (the polled price), which only reads the feed rounds published after the last one seen.
 */
export const useRoundPriceHistory = (since, tick) => {
  const cache = useRef({ since: null, lastId: ethers.constants.Zero, points: [] });
  const [points, setPoints] = useState([]);

  useEffect(() => {
    if (!since) return undefined;
    let active = true;
    (async () => {
      try {
        const { feed, decimals } = await getFeed();
        const latest = await feed.latestRoundData();
        const known = cache.current.since === since ? cache.current : { since, lastId: ethers.constants.Zero, points: [] };
        if (known.points.length && known.lastId.gte(latest.roundId)) return;
        const fresh = await walkBack(feed, decimals, latest.roundId, known.lastId, since);
        // a feed that answers every round with the latest price and time (test mocks) has no history to show
        if (fresh.length > 2 && new Set(fresh.map((point) => point.t)).size === 1) {
          feedPromise = Promise.reject(new Error('feed without history'));
          feedPromise.catch(() => {});
          return;
        }
        if (!active) return;
        cache.current = { since, lastId: latest.roundId, points: [...known.points, ...fresh] };
        setPoints(cache.current.points);
      } catch (error) {
        // no feed history: the chart keeps the prices polled while the page is open
      }
    })();
    return () => {
      active = false;
    };
  }, [since, tick]);

  return cache.current.since === since ? points : [];
};
